import API from './index';
import AppUrls from '../config/AppUrls';
import { buildPathWithParams } from '../utils/Paths';
import { GLOBAL } from '../utils/Constants';
import { prepareExportFilters, statusMappingOrder } from '../utils/Constants/PaymentOrdersConstants';

class TransactionsService {
  static fetchCashIn(filters) {
    return API.get(AppUrls.api.cashIn, filters).then((response) => response.data);
  }

  static fetchCashOut(filters) {
    return API.get(AppUrls.api.cashOut, filters).then((response) => response.data);
  }

  static fetchAllTransactions(filters) {
    return API.get(AppUrls.api.allTransactions, filters).then((response) => response.data);
  }

  static fetchRefund(filters) {
    return API.get(AppUrls.api.refund, filters).then((response) => response.data);
  }

  static fetchRefundDetails(idRefund, isRejected) {
    let path = AppUrls.api.refundDetails;
    if (isRejected) {
      path = AppUrls.api.refundDetailsRejected;
    }
    return API.get(buildPathWithParams(path, { idRefund })).then((response) => response.data);
  }

  static fetchBalanceReport(filters) {
    return API.get(AppUrls.api.balanceReport, filters).then((response) => response.data);
  }

  static exportBalanceReport(filters) {
    return API.get(AppUrls.api.balanceReportExport, filters).then((response) => response.data.url);
  }

  static fetchBalanceWalletReport(filters) {
    return API.get(AppUrls.api.balanceWalletReport, filters).then((response) => response.data);
  }

  static exportBalanceWalletReport(filters) {
    return API.get(AppUrls.api.balanceWalletReportExport, filters).then((response) => response.data.url);
  }

  static fetchRejectedTransactionDetails(idTransaction, type) {
    const partialPath = type === GLOBAL.CASHIN ? AppUrls.api.cashInRejected : AppUrls.api.cashOutRejected;
    const path = buildPathWithParams(partialPath, { idTransaction });
    return API.get(path).then((response) => response.data);
  }

  static fetchTransactionDetails(idTransaction, type, isRejected) {
    if (isRejected) {
      return TransactionsService.fetchRejectedTransactionDetails(idTransaction, type);
    }

    const partialPath = type === GLOBAL.CASHIN ? AppUrls.api.cashInDetails : AppUrls.api.cashOutDetails;
    const path = buildPathWithParams(partialPath, { idTransaction });
    return API.get(path).then((response) => response.data);
  }

  static fetchInstallmentsReleases(idDeposit) {
    const path = buildPathWithParams(AppUrls.api.installmentsReleases, { idDeposit });
    return API.get(path).then((response) => response.data);
  }

  static flagCashInForReview(idDeposit) {
    return API.post(buildPathWithParams(AppUrls.api.cashInFlagForReview, { idTransaction: idDeposit })).then((response) => response.data);
  }

  static reversalCashInForReview(idDeposit) {
    return API.post(buildPathWithParams(AppUrls.api.cashInReversalForReview, { idTransaction: idDeposit })).then((response) => response.data);
  }

  static cashInManualRefund(idTransaction, data) {
    return API.post(buildPathWithParams(AppUrls.api.cashInManualRefund, { idTransaction }), data).then((response) => response.data);
  }

  static fetchAccountTypes(countryIso) {
    const path = buildPathWithParams(AppUrls.api.cashInAccountTypes, { countryIso });
    return API.get(path).then((response) => response.data);
  }

  static flagCashOutForReview(idDeposit) {
    return API.post(buildPathWithParams(AppUrls.api.cashOutFlagForReview, { idTransaction: idDeposit })).then((response) => response.data);
  }

  static fetchCountries(transactionType) {
    const path = buildPathWithParams(AppUrls.api.transactionsCountries, { transactionType });
    return API.get(path).then((response) => response.data);
  }

  static fetchRejectedTransactionHistory(idTransaction, type) {
    let partialPath;
    if (type === GLOBAL.CASHIN) {
      partialPath = AppUrls.api.cashInRejectedHistory;
    } else if (type === GLOBAL.REFUND) {
      partialPath = AppUrls.api.refundRejectedHistory;
    } else {
      partialPath = AppUrls.api.cashOutRejectedHistory;
    }
    const path = buildPathWithParams(partialPath, { idTransaction });
    return API.get(path).then((response) => response.data);
  }

  static fetchTransactionHistory(idTransaction, type = GLOBAL.CASHIN, isRejected = false) {
    if (isRejected) {
      return TransactionsService.fetchRejectedTransactionHistory(idTransaction, type);
    }
    let partialPath;
    if (type === GLOBAL.CASHIN) {
      partialPath = AppUrls.api.cashInHistory;
    } else if (type === GLOBAL.REFUND) {
      partialPath = AppUrls.api.refundHistory;
    } else {
      partialPath = AppUrls.api.cashOutHistory;
    }
    const path = buildPathWithParams(partialPath, { idTransaction });
    return API.get(path).then((response) => response.data);
  }

  static fetchLogDetails(idLog, type = GLOBAL.CASHIN, direction = 'in') {
    let partialPath;
    if (type === GLOBAL.CASHIN) {
      partialPath = AppUrls.api.cashInLogDetails;
    } else if (type === GLOBAL.REFUND) {
      partialPath = AppUrls.api.refundLogDetails;
    } else {
      partialPath = AppUrls.api.cashOutLogDetails;
    }
    const path = buildPathWithParams(partialPath, { idLog, direction });
    return API.get(path).then((response) => response.data);
  }

  static fetchExtraInfo(idTransaction) {
    const path = buildPathWithParams(AppUrls.api.cashInExtra, { idTransaction });
    return API.get(path).then((response) => response.data);
  }

  static exportTransactionList(type, filters) {
    let path = AppUrls.api.allTransactionsExport;

    if (type === 'recurringpayments') {
      return API.get(AppUrls.api.recurringPaymentsExport, filters).then((response) => response);
    }

    if (type === GLOBAL.CASHIN) {
      path = AppUrls.api.cashInExport;
    } else if (type === GLOBAL.CASHOUT) {
      path = AppUrls.api.cashOutExport;
    } else if (type === 'allclients') {
      path = AppUrls.api.clientExport;
    } else if (type === 'allnotes') {
      path = AppUrls.api.notesExport;
    } else if (type === 'allpaymentorderslist') {
      path = AppUrls.api.paymentOrderExport;
      filters = prepareExportFilters(filters, statusMappingOrder);
    }

    return API.get(path, filters).then((response) => response);
  }

  static export(transactionType, filters = {}) {
    const path = buildPathWithParams(AppUrls.api.transactionsExport, { transactionType });
    return API.get(path, filters).then((response) => response.data.url);
  }

  static resendNotification(idTransaction, type = GLOBAL.CASHIN) {
    let partialPath;
    if (type === GLOBAL.CASHIN) {
      partialPath = AppUrls.api.cashInResend;
    } else if (type === GLOBAL.REFUND) {
      partialPath = AppUrls.api.refundResend;
    } else {
      partialPath = AppUrls.api.cashOutResend;
    }
    const path = buildPathWithParams(partialPath, { idTransaction });
    return API.put(path).then((response) => response.data);
  }

  static requestReceipt(idTransaction) {
    return API.post(AppUrls.api.cashOutRequestReceipt, idTransaction).then((response) => response.data);
  }

  static fetchMerchantNotificationUrl() {
    return API.get(AppUrls.api.cashOutNotificationUrl).then((response) => response.data);
  }

  static fetchDocumentTypes(countryIso) {
    const path = buildPathWithParams(AppUrls.api.cashOutDocumentTypes, { countryIso });
    return API.get(path).then((response) => response.data);
  }

  static fetchRefundAccountTypes(country) {
    const payload = {
      ...(country && { country }),
    };
    return API.get(AppUrls.api.refundAccountTypes, payload).then((response) => response.data);
  }

  static fetchCashInDocumentTypes(countryIso) {
    const path = buildPathWithParams(AppUrls.api.cashInDocumentTypes, { countryIso });
    return API.get(path).then((response) => response.data);
  }

  static fetchBankCodes(countryIso) {
    const path = buildPathWithParams(AppUrls.api.cashOutBankCodes, { countryIso });
    return API.get(path).then((response) => response.data);
  }

  static fetchRefundBankCodes(countryIso) {
    const path = buildPathWithParams(AppUrls.api.refundBankCodes, { countryIso });
    return API.get(path).then((response) => response.data);
  }

  static fetchRefundDocumentTypes(countryIso) {
    const path = buildPathWithParams(AppUrls.api.refundDocumentTypes, { countryIso });
    return API.get(path).then((response) => response.data);
  }

  static createCashoutRequest(data) {
    return API.post(AppUrls.api.cashOutRequestManual, { ...data }).then((response) => {
      return response.data;
    });
  }

  static async searchCashoutBeneficiary(search) {
    const response = await TransactionsService.fetchCashoutBeneficiary({ search });
    return response.data;
  }

  static fetchCashoutBeneficiary(filters) {
    return API.get(AppUrls.api.cashOutBeneficiaries, filters).then((response) => {
      return response.data;
    });
  }

  static createCashInRequest(data) {
    return API.post(AppUrls.api.cashInRequestManual, { ...data }).then((response) => {
      return response.data;
    });
  }

  static createRecurringDepositRequest(data) {
    return API.post(AppUrls.api.recurringDepositRequest, data).then((response) => response.data);
  }

  static fetchChargeback(filters) {
    return API.get(AppUrls.api.chargeback, filters).then((response) => response.data);
  }

  static changeCashinStatus(idDeposit, status) {
    const path = buildPathWithParams(AppUrls.api.cashInChangeStatus, { idDeposit, status });
    return API.post(path).then((response) => response.data);
  }

  static changeCashoutStatus(idTransaction, status) {
    let path = buildPathWithParams(AppUrls.api.cashOutChangeStatus, { idTransaction, status });
    path = `${path}?status=${status}`;
    return API.post(path).then((response) => response.data);
  }

  static changeRefundStatus(idTransaction, status) {
    const path = buildPathWithParams(AppUrls.api.refundChangeStatus, { idTransaction, status });
    return API.put(path).then((response) => response.data);
  }

  static changeCashoutOnHold(idTransaction) {
    const path = buildPathWithParams(AppUrls.api.cashOutOnHoldStatus, { idTransaction });
    return API.post(path).then((response) => response.data);
  }

  static exportChargebacks(filters) {
    return API.get(AppUrls.api.chargebacksExport, filters).then((response) => response.data.url);
  }

  static downloadReceipt(idTransaction, url) {
    const path = buildPathWithParams(url, { idTransaction });
    return API.get(path).then((response) => response.data);
  }

  static discardVoucher(idDeposit, confirmData) {
    const path = buildPathWithParams(AppUrls.api.cashInDiscardVoucher, { idDeposit });
    return API.put(path, confirmData).then((response) => response.data);
  }

  static fetchCashInPendingVouchers() {
    return API.get(AppUrls.api.cashInPendingVouchers).then((response) => response.data);
  }

  static resendSmsNotification(idTransaction) {
    const path = buildPathWithParams(AppUrls.api.cashOutResendSmsNotification, { idTransaction });
    return API.post(path);
  }

  static cancelWithdrawal(idTransaction) {
    const path = buildPathWithParams(AppUrls.api.cashOutCancel, { idTransaction });
    return API.delete(path);
  }

  static quickPayInformation(idTransaction) {
    const path = buildPathWithParams(AppUrls.api.quickPayInformation, { idTransaction });
    return API.get(path).then((response) => response.data);
  }

  static getRefundVoucher(idRefund) {
    const path = buildPathWithParams(AppUrls.api.refundRefoundVoucher, { idRefund });
    return API.get(path).then((response) => response.data);
  }
}

export default TransactionsService;
